export interface MinVertexCoverConflict<T> {
  delta: number;
  item: T;
}

export interface MinVertexCoverGroup<T> {
  anchor: T;
  conflicts: Array<MinVertexCoverConflict<T>>;
}

// A gap only grows the farther apart two sorted values are, so `right` never moves backward. O(n).
export function findNearestExceedingGap(sortedValues: Array<number>, threshold: number): Array<number> {
  const n = sortedValues.length;
  const nearestRight = new Array<number>(n).fill(-1);

  for (let i = 0, right = 0; i < n; i++) {
    if (right <= i) {
      right = i + 1;
    }
    while (right < n && sortedValues[right] - sortedValues[i] < threshold) {
      right++;
    }
    nearestRight[i] = right < n ? right : -1;
  }

  return nearestRight;
}

// Minimum vertex cover of the graph where an edge joins items whose values differ by at least
// `threshold`. Exact, not approximate — every neighborhood is a prefix-plus-suffix of the
// sorted order, so a greedy degree-bucket peel (Dial's algorithm / k-core decomposition) hits
// the true minimum, unlike general Set Cover's ln(n) bound. O(n log n) sort + O(n + edges) peel.
export function minVertexCoverByThreshold<T>(
  items: Array<T>,
  getValue: (item: T) => number,
  threshold: number,
): Array<MinVertexCoverGroup<T>> {
  const sorted = [...items].sort((a, b) => getValue(a) - getValue(b));
  const n = sorted.length;
  const nearestRight = findNearestExceedingGap(sorted.map(getValue), threshold);

  const remainingConflicts = new Map<number, Set<number>>(Array.from({ length: n }, (_, i) => [i, new Set<number>()]));

  for (let i = 0; i < n; i++) {
    if (nearestRight[i] === -1) {
      continue;
    }

    for (let j = nearestRight[i]; j < n; j++) {
      remainingConflicts.get(i)?.add(j);
      remainingConflicts.get(j)?.add(i);
    }
  }

  const degree = new Array<number>(n);
  const buckets: Array<Set<number>> = Array.from({ length: n }, () => new Set<number>());

  for (let i = 0; i < n; i++) {
    degree[i] = remainingConflicts.get(i)?.size ?? 0;
    buckets[degree[i]].add(i);
  }

  const groups: Array<MinVertexCoverGroup<T>> = [];
  let currentMax = n - 1;

  while (currentMax > 0) {
    while (currentMax > 0 && buckets[currentMax].size === 0) {
      currentMax--;
    }

    if (currentMax === 0) {
      break;
    }

    const anchorIndex = Math.min(...buckets[currentMax]);

    buckets[currentMax].delete(anchorIndex);

    const conflictIndices = [...(remainingConflicts.get(anchorIndex) ?? [])].sort((a, b) => a - b);

    for (const conflictIndex of conflictIndices) {
      remainingConflicts.get(anchorIndex)?.delete(conflictIndex);
      remainingConflicts.get(conflictIndex)?.delete(anchorIndex);
      buckets[degree[conflictIndex]].delete(conflictIndex);
      degree[conflictIndex]--;
      buckets[degree[conflictIndex]].add(conflictIndex);
    }

    groups.push({
      anchor: sorted[anchorIndex],
      conflicts: conflictIndices.map((conflictIndex) => ({
        delta: Math.abs(getValue(sorted[conflictIndex]) - getValue(sorted[anchorIndex])),
        item: sorted[conflictIndex],
      })),
    });
  }

  return groups.sort((a, b) => b.conflicts.length - a.conflicts.length);
}
